import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { currentTenant, getAccessToken } from "@/auth/session";

/** RFC 9457 problem details, as every API error is returned. */
export interface Problem {
  type?: string;
  title: string;
  status: number;
  detail?: string | null;
  /** Why access was refused: sign_in, no_account, no_tenant, not_member, suspended... */
  code?: string | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.problem = problem;
  }

  get code(): string | null {
    return this.problem.code ?? null;
  }
}

/** Sent on a request that must go without anyone's sign-in (accepting an invite by password). */
export const ANONYMOUS = { "X-ZeaOps-Anonymous": "1" };

const auth: Middleware = {
  async onRequest({ request }) {
    if (request.headers.get("X-ZeaOps-Anonymous")) {
      request.headers.delete("X-ZeaOps-Anonymous");
      return request;
    }
    const token = await getAccessToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    const tenant = currentTenant();
    if (tenant) request.headers.set("X-ZeaOps-Tenant", tenant);
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: "" });
api.use(auth);

/** Unwraps an openapi-fetch result, throwing an ApiError for any problem response. */
export async function call<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (response.ok && data !== undefined) return data;
  const body = (error ?? {}) as Partial<Problem>;
  throw new ApiError({
    title: body.title ?? "Request failed",
    status: response.status,
    detail: body.detail ?? null,
    code: body.code ?? response.headers.get("X-ZeaOps-Code"),
  });
}
